import type { ComponentNode } from "../layout/types.js";
import { applyPatchToIconNode } from "./applyRuntimePatches.js";
import { compileBehavior } from "./compileBehavior.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { evaluateWithRenderCache } from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import { compileNodeStyle, transitionsTouchTopLeft } from "./helpers.js";
import { uniqueDependencies } from "./method.js";
import type {
  CompiledIconNode,
  CompileNodeContext,
  RenderIconNode,
  RenderIconProps,
  RuntimeContext,
} from "./types.js";

const DEFAULT_ICON_NAME = "Star";

function normalizeIconProps(value: unknown): RenderIconProps {
  const props =
    value != null && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const size = Number(props.size);
  return {
    iconName:
      typeof props.iconName === "string" && props.iconName.trim().length > 0
        ? props.iconName.trim()
        : DEFAULT_ICON_NAME,
    color:
      typeof props.color === "string" && props.color.trim().length > 0
        ? props.color.trim()
        : null,
    size: Number.isFinite(size) ? Math.max(1, Math.round(size)) : 48,
  };
}

export function compileIconNode(
  node: ComponentNode,
  compileContext: CompileNodeContext,
): CompiledIconNode {
  if (node.kind !== "component" || node.component !== "icon") {
    throw new Error("Node is not an icon component");
  }

  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const props = normalizeIconProps(node.props);
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;

  const dependencies = uniqueDependencies(
    behavior ? collectBehaviorDependencies(behavior, compileContext) : [],
  );

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderIconNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        let renderNode: RenderIconNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          zIndex: node.zIndex,
          rotation: node.rotation ?? 0,
          kind: "icon",
          props: { ...props },
          style: { ...baseStyle },
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToIconNode(renderNode, patch);
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
